/** Phase 2E-camp-2 — operator-to-campaign assignment surface.
 *
 * Lists the current assignees for a campaign + lets a user with
 * `campaign.update` add or remove. Empty list means the campaign is
 * "open to all" (back-compat — every inspector with campaign.read
 * sees it). Adding the first assignee narrows visibility per
 * docs/CAMPAIGN_OPERATOR_ASSIGNMENT.md.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PermissionGate } from "@/components/PermissionGate";

import { AddAssigneeModal } from "./AddAssigneeModal";
import { listAssignees, removeAssignee } from "./assigneesApi";

export type { Assignee } from "./assigneesApi";


export function CampaignAssigneesCard({ campaignId }: { campaignId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);

  const list = useQuery({
    queryKey: ["campaign-assignees", campaignId],
    queryFn: () => listAssignees(campaignId),
  });

  const remove = useMutation({
    mutationFn: (userId: number) => removeAssignee(campaignId, userId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["campaign-assignees", campaignId],
      });
    },
  });

  const count = list.data?.length ?? 0;

  return (
    <section className="card space-y-3" data-testid="assignees-card">
      <header className="flex items-baseline justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("ui.campaignAssignees.title")}
          </h3>
          <p className="text-xs text-nb-text-3">
            {count === 0
              ? t("ui.campaignAssignees.empty")
              : t("ui.campaignAssignees.assignedCount", { count })}
          </p>
        </div>
        <PermissionGate perm="campaign.members.manage">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => setShowAdd(true)}
            data-testid="assignees-add-button"
          >
            {t("ui.campaignAssignees.addOperator")}
          </button>
        </PermissionGate>
      </header>

      {list.isLoading && (
        <p className="text-xs text-nb-text-3">{t("ui.campaignAssignees.loading")}</p>
      )}

      {list.data && list.data.length > 0 && (
        <ul className="divide-y divide-nb-border">
          {list.data.map((a) => {
            const name =
              [a.first_name, a.last_name].filter(Boolean).join(" ") || null;
            return (
              <li
                key={a.id}
                className="flex items-center justify-between py-2 text-sm"
              >
                <div>
                  <p className="text-nb-text">{name ?? a.email}</p>
                  {name && (
                    <p className="text-xs text-nb-text-3">{a.email}</p>
                  )}
                </div>
                <PermissionGate perm="campaign.members.manage">
                  <button
                    type="button"
                    className="btn-ghost text-xs"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(a.user_id)}
                    aria-label={t("ui.campaignAssignees.removeAria", { email: a.email })}
                  >
                    {t("ui.campaignAssignees.remove")}
                  </button>
                </PermissionGate>
              </li>
            );
          })}
        </ul>
      )}

      {showAdd && (
        <AddAssigneeModal
          campaignId={campaignId}
          existing={new Set((list.data ?? []).map((a) => a.user_id))}
          onClose={() => setShowAdd(false)}
        />
      )}
    </section>
  );
}
